import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Sun, Wind, Thermometer, Target, FileCheck, Award, Headphones, Shield,
  ClipboardCheck, LineChart, Layers, Wrench, Activity, Phone, Mail, MapPin,
  ArrowRight, Check, Menu, X, ChevronDown
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { useSmoothScroll } from '../hooks/useSmoothScroll';
import WhatsAppButton from '../components/WhatsAppButton';
import FAQSection from '../components/FAQSection';
import { soluzioni, metodoPEP, percheSceglierci, faqData, tipiInteresse, encode } from '../newMockData';
import '../premium.css';

const iconMap = {
  Sun, Wind, Thermometer, Target, FileCheck, Award, Headphones, Shield,
  ClipboardCheck, LineChart, Layers, Wrench, Activity
};

const PremiumHomePage = () => {
  useSmoothScroll();
  
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [formData, setFormData] = useState({
    nome: '',
    telefono: '',
    email: '',
    comune: '',
    provincia: '',
    tipoInteresse: '',
    consumoEnergetico: '',
    messaggio: '',
    privacy: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    document.title = "Alt Bollette | Soluzioni energetiche su misura";
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.privacy) {
      toast.error("Devi accettare la privacy policy per continuare");
      return;
    }

    // Client-side validation (Select component does not enforce native "required")
    if (!formData.tipoInteresse) {
      toast.error("Seleziona il tipo di interesse per continuare");
      return;
    }
    if (formData.telefono.trim().length < 8) {
      toast.error("Inserisci un numero di telefono valido (minimo 8 cifre)");
      return;
    }
    if (formData.provincia.trim().length !== 2) {
      toast.error("Inserisci la sigla della provincia (2 lettere, es. MI)");
      return;
    }

    setIsSubmitting(true);
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || '';
      
      // Prepare data for backend
      const leadData = {
        nome: formData.nome,
        telefono: formData.telefono,
        email: formData.email,
        comune: formData.comune,
        provincia: formData.provincia,
        tipo_interesse: formData.tipoInteresse,
        consumo_energetico: formData.consumoEnergetico || '',
        messaggio: formData.messaggio || '',
        consenso_privacy: formData.privacy,
        bot_field: '' // Honeypot field
      };
      
      const response = await fetch(`${API_URL}/api/leads`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(leadData)
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        let message = 'Errore durante l\'invio. Riprova più tardi.';
        if (Array.isArray(errorData.detail)) {
          // Pydantic 422 validation errors -> friendly Italian message
          const fieldLabels = {
            nome: 'Nome e Cognome', telefono: 'Telefono', email: 'Email',
            comune: 'Comune', provincia: 'Provincia', tipo_interesse: 'Tipo di interesse',
            consumo_energetico: 'Consumo energetico', messaggio: 'Messaggio'
          };
          const fields = errorData.detail
            .map(err => fieldLabels[err.loc && err.loc[1]])
            .filter(Boolean);
          message = fields.length
            ? `Verifica i seguenti campi: ${[...new Set(fields)].join(', ')}`
            : 'Alcuni campi non sono validi. Controlla e riprova.';
        } else if (typeof errorData.detail === 'string' && errorData.detail) {
          message = errorData.detail;
        }
        throw new Error(message);
      }
      
      const result = await response.json();
      
      // Success - show confirmation only if server responded positively
      toast.success("Richiesta ricevuta. Un consulente Alt Bollette ti contatterà entro 24 ore.");
      
      // Reset form
      setFormData({
        nome: '', telefono: '', email: '', comune: '', provincia: '',
        tipoInteresse: '', consumoEnergetico: '', messaggio: '', privacy: false
      });
      
    } catch (error) {
      console.error('Form submission error:', error);
      toast.error(error.message || "Si è verificato un errore. Riprova più tardi o contattaci telefonicamente.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToForm = () => {
    document.getElementById('contatto')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-white">
      <WhatsAppButton />

      {/* Navigation */}
      <motion.nav 
        className={`premium-nav ${isScrolled ? 'scrolled' : ''}`}
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, type: "spring" }}
      >
        <div className="container mx-auto px-6">
          <div className="flex items-center justify-between h-20">
            <motion.div 
              className="flex items-center gap-3"
              whileHover={{ scale: 1.05 }}
            >
              <img 
                src="/alt-bollette-logo.png" 
                alt="Alt Bollette"
                className="h-12 w-auto"
              />
            </motion.div>

            <div className="hidden md:flex items-center gap-8">
              <a href="#soluzioni" className="text-gray-600 hover:text-[#2d5f3f] transition-colors font-medium">
                Soluzioni
              </a>
              <a href="#metodo" className="text-gray-600 hover:text-[#2d5f3f] transition-colors font-medium">
                Metodo PEP
              </a>
              <a href="#perche" className="text-gray-600 hover:text-[#2d5f3f] transition-colors font-medium">
                Perché Noi
              </a>
              <a href="#faq" className="text-gray-600 hover:text-[#2d5f3f] transition-colors font-medium">
                FAQ
              </a>
              <Button onClick={scrollToForm} className="btn-premium-primary">
                Richiedi Consulenza
              </Button>
            </div>

            <button 
              className="md:hidden p-2"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              {isMenuOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>

          {isMenuOpen && (
            <motion.div 
              className="md:hidden py-6 space-y-4"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
            >
              <a href="#soluzioni" className="block py-2" onClick={() => setIsMenuOpen(false)}>Soluzioni</a>
              <a href="#metodo" className="block py-2" onClick={() => setIsMenuOpen(false)}>Metodo PEP</a>
              <a href="#perche" className="block py-2" onClick={() => setIsMenuOpen(false)}>Perché Noi</a>
              <a href="#faq" className="block py-2" onClick={() => setIsMenuOpen(false)}>FAQ</a>
              <Button onClick={() => { scrollToForm(); setIsMenuOpen(false); }} className="btn-premium-primary w-full">
                Richiedi Consulenza
              </Button>
            </motion.div>
          )}
        </div>
      </motion.nav>

      {/* Hero Section */}
      <section className="hero-premium pt-32 pb-20 px-6">
        <div className="container mx-auto max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <motion.h1 
                className="hero-title-premium"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                Il tuo <span className="highlight">progetto energetico</span>, costruito sulle tue esigenze
              </motion.h1>
              
              <motion.p 
                className="hero-subtitle-premium"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.3 }}
              >
                Analizziamo consumi, caratteristiche dell'immobile e obiettivi per progettare soluzioni 
                fotovoltaiche, microeoliche e termiche realmente adatte al cliente.
              </motion.p>

              <motion.div 
                className="flex flex-col sm:flex-row gap-4"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
              >
                <Button onClick={scrollToForm} className="btn-premium-primary">
                  Richiedi uno studio di fattibilità
                  <ArrowRight className="w-5 h-5" />
                </Button>
                <a href="https://wa.me/393501483135" target="_blank" rel="noopener noreferrer">
                  <Button className="btn-premium-secondary w-full sm:w-auto">
                    Parla con un consulente su WhatsApp
                  </Button>
                </a>
              </motion.div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="relative"
            >
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img 
                  src="https://images.unsplash.com/photo-1508514177221-188b1cf16e9d"
                  alt="Impianto fotovoltaico professionale"
                  className="w-full h-[500px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
              </div>

              <motion.div
                className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-6 shadow-xl hidden md:block"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.6 }}
              >
                <div className="text-4xl font-bold" style={{ color: '#2d5f3f' }}>500+</div>
                <div className="text-gray-600 font-medium">Progetti Realizzati</div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Marquee Section */}
      <div className="marquee-section">
        <div className="marquee-content">
          {[...Array(4)].map((_, i) => (
            <React.Fragment key={i}>
              <div className="marquee-item">● Fotovoltaico</div>
              <div className="marquee-item">● Microeolico</div>
              <div className="marquee-item">● Pompe di Calore</div>
              <div className="marquee-item">● Studio di Fattibilità</div>
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Soluzioni Section */}
      <section id="soluzioni" className="py-24 px-6 bg-gray-50">
        <div className="container mx-auto max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4" style={{ color: '#0a3d2a' }}>
              Soluzioni per Famiglie e Imprese
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Progetti energetici personalizzati con tecnologie innovative e studio di fattibilità professionale
            </p>
          </motion.div>

          <div className="bento-grid">
            {soluzioni.map((soluzione, index) => {
              const IconComponent = iconMap[soluzione.icon];
              return (
                <motion.div
                  key={soluzione.id}
                  className="bento-card"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <img 
                    src={soluzione.image}
                    alt={soluzione.titolo}
                    className="bento-card-image"
                  />
                  
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-xl bg-[#e8f5e9] flex items-center justify-center">
                      <IconComponent className="w-6 h-6" style={{ color: '#2d5f3f' }} />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold" style={{ color: '#0a3d2a' }}>
                        {soluzione.titolo}
                      </h3>
                      <p className="text-sm text-gray-500">{soluzione.sottotitolo}</p>
                    </div>
                  </div>

                  <p className="text-gray-600 mb-4 leading-relaxed">
                    {soluzione.descrizione}
                  </p>

                  <ul className="space-y-2">
                    {soluzione.benefici.map((beneficio, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                        <Check className="w-5 h-5 flex-shrink-0" style={{ color: '#2d5f3f' }} />
                        <span>{beneficio}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Metodo PEP */}
      <section id="metodo" className="py-24 px-6">
        <div className="container mx-auto max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4" style={{ color: '#0a3d2a' }}>
              Il Metodo PEP
            </h2>
            <p className="text-xl font-semibold mb-2" style={{ color: '#2d5f3f' }}>
              Progetto Energetico Personalizzato
            </p>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Un processo strutturato in 5 fasi per garantire il massimo risultato
            </p>
          </motion.div>

          <div className="process-timeline">
            {metodoPEP.map((step, index) => {
              const IconComponent = iconMap[step.icon];
              const isEven = index % 2 === 0;
              
              return (
                <motion.div
                  key={step.numero}
                  className="process-step"
                  initial={{ opacity: 0, x: isEven ? -50 : 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                >
                  {isEven ? (
                    <>
                      <div className="text-right pr-8 hidden md:block">
                        <h3 className="text-2xl font-bold mb-2" style={{ color: '#0a3d2a' }}>
                          {step.titolo}
                        </h3>
                        <p className="text-gray-600">{step.descrizione}</p>
                      </div>
                      <div className="process-step-number">{step.numero}</div>
                      <div className="md:hidden pl-8">
                        <h3 className="text-2xl font-bold mb-2" style={{ color: '#0a3d2a' }}>
                          {step.titolo}
                        </h3>
                        <p className="text-gray-600">{step.descrizione}</p>
                      </div>
                      <div className="hidden md:block"></div>
                    </>
                  ) : (
                    <>
                      <div className="hidden md:block"></div>
                      <div className="process-step-number">{step.numero}</div>
                      <div className="pl-8">
                        <h3 className="text-2xl font-bold mb-2" style={{ color: '#0a3d2a' }}>
                          {step.titolo}
                        </h3>
                        <p className="text-gray-600">{step.descrizione}</p>
                      </div>
                    </>
                  )}
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>


      {/* Perché Sceglierci */}
      <section id="perche" className="py-24 px-6 bg-gray-50">
        <div className="container mx-auto max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4" style={{ color: '#0a3d2a' }}>
              Perché Scegliere Alt Bollette
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Professionalità, trasparenza e risultati concreti: ecco cosa ci distingue
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {percheSceglierci.map((item, index) => {
              const IconComponent = iconMap[item.icon];
              return (
                <motion.div
                  key={index}
                  className="bento-card"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <div className="w-14 h-14 rounded-xl bg-[#e8f5e9] flex items-center justify-center mb-4">
                    <IconComponent className="w-7 h-7" style={{ color: '#2d5f3f' }} />
                  </div>
                  <h3 className="text-xl font-bold mb-3" style={{ color: '#0a3d2a' }}>
                    {item.titolo}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {item.descrizione}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <FAQSection faqs={faqData} />

      {/* Contact Form */}
      <section id="contatto" className="py-24 px-6 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4" style={{ color: '#0a3d2a' }}>
              Richiedi una Consulenza
            </h2>
            <p className="text-lg text-gray-600">
              Compila il form per ricevere uno studio di fattibilità personalizzato
            </p>
          </motion.div>

          <motion.div
            className="premium-form"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <form 
              onSubmit={handleSubmit}
              data-testid="contact-form"
            >
              {/* Honeypot field for spam protection */}
              <input 
                type="text" 
                name="bot_field" 
                style={{ display: 'none' }} 
                tabIndex="-1" 
                autoComplete="off"
              />

              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <Label htmlFor="nome">Nome e Cognome *</Label>
                  <Input
                    id="nome"
                    name="nome"
                    type="text"
                    required
                    data-testid="form-nome-input"
                    value={formData.nome}
                    onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                    placeholder="Mario Rossi"
                  />
                </div>

                <div>
                  <Label htmlFor="telefono">Telefono *</Label>
                  <Input
                    id="telefono"
                    name="telefono"
                    type="tel"
                    required
                    data-testid="form-telefono-input"
                    value={formData.telefono}
                    onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                    placeholder="+39 333 1234567"
                  />
                </div>
              </div>

              <div className="mb-6">
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  required
                  data-testid="form-email-input"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="mario.rossi@email.it"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <Label htmlFor="comune">Comune *</Label>
                  <Input
                    id="comune"
                    name="comune"
                    type="text"
                    required
                    data-testid="form-comune-input"
                    value={formData.comune}
                    onChange={(e) => setFormData({ ...formData, comune: e.target.value })}
                    placeholder="Milano"
                  />
                </div>

                <div>
                  <Label htmlFor="provincia">Provincia *</Label>
                  <Input
                    id="provincia"
                    name="provincia"
                    type="text"
                    required
                    maxLength={2}
                    data-testid="form-provincia-input"
                    value={formData.provincia}
                    onChange={(e) => setFormData({ ...formData, provincia: e.target.value.toUpperCase() })}
                    placeholder="MI"
                  />
                </div>
              </div>

              <div className="mb-6">
                <Label htmlFor="tipoInteresse">Tipo di Interesse *</Label>
                <Select 
                  value={formData.tipoInteresse} 
                  onValueChange={(value) => setFormData({ ...formData, tipoInteresse: value })}
                  required
                >
                  <SelectTrigger id="tipoInteresse" name="tipoInteresse" data-testid="form-tipo-interesse-select">
                    <SelectValue placeholder="Seleziona il tuo interesse..." />
                  </SelectTrigger>
                  <SelectContent>
                    {tipiInteresse.map((tipo) => (
                      <SelectItem key={tipo} value={tipo}>{tipo}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="mb-6">
                <Label htmlFor="consumoEnergetico">Consumo o Spesa Energetica Indicativa</Label>
                <Input
                  id="consumoEnergetico"
                  name="consumoEnergetico"
                  type="text"
                  value={formData.consumoEnergetico}
                  onChange={(e) => setFormData({ ...formData, consumoEnergetico: e.target.value })}
                  placeholder="es: 3000 kWh/anno o 150€/mese"
                />
              </div>

              <div className="mb-6">
                <Label htmlFor="messaggio">Messaggio (Facoltativo)</Label>
                <Textarea
                  id="messaggio"
                  name="messaggio"
                  value={formData.messaggio}
                  onChange={(e) => setFormData({ ...formData, messaggio: e.target.value })}
                  placeholder="Descrivi brevemente le tue esigenze o domande..."
                  rows={4}
                />
              </div>

              <div className="flex items-start gap-3 mb-6">
                <Checkbox
                  id="privacy"
                  data-testid="form-privacy-checkbox"
                  checked={formData.privacy}
                  onCheckedChange={(checked) => setFormData({ ...formData, privacy: checked })}
                />
                <Label htmlFor="privacy" className="text-sm leading-relaxed cursor-pointer">
                  Accetto la <a href="/privacy-policy" className="text-[#2d5f3f] hover:underline">Privacy Policy</a> e 
                  autorizzo il trattamento dei miei dati per ricevere la consulenza richiesta *
                </Label>
              </div>

              <Button 
                type="submit" 
                disabled={isSubmitting || !formData.privacy}
                className="btn-premium-primary w-full"
                data-testid="form-submit-button"
              >
                {isSubmitting ? 'Invio in corso...' : 'Invia Richiesta'}
                {!isSubmitting && <ArrowRight className="w-5 h-5 ml-2" />}
              </Button>

              <p className="text-sm text-gray-500 text-center mt-4">
                Ti contatteremo entro 24 ore per un primo confronto
              </p>
            </form>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="premium-footer">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            <div>
              <div className="bg-white rounded-lg p-3 inline-block mb-4">
                <img 
                  src="/alt-bollette-logo.png" 
                  alt="Alt Bollette"
                  className="h-12 w-auto"
                />
              </div>
              <p className="text-gray-300 leading-relaxed">
                Soluzioni energetiche su misura per famiglie e imprese
              </p>
            </div>

            <div>
              <h3 className="text-lg font-bold mb-4">Azienda</h3>
              <p className="text-sm mb-2 font-semibold">Alt Bollette SRL</p>
              <p className="text-sm text-gray-300">
                Corso Mazzini 76<br />
                48022 Lugo (RA)
              </p>
            </div>

            <div>
              <h3 className="text-lg font-bold mb-4">Contatti</h3>
              <div className="space-y-2">
                <a href="tel:+393501483135" className="flex items-center gap-2 text-sm hover:text-white">
                  <Phone className="w-4 h-4" />
                  +39 350 148 3135
                </a>
                <a href="https://wa.me/393501483135" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm hover:text-white">
                  WhatsApp
                </a>
                <a href="mailto:amministrazione@altbollette.info" className="flex items-center gap-2 text-sm hover:text-white">
                  <Mail className="w-4 h-4" />
                  amministrazione@altbollette.info
                </a>
                <a href="https://altbollette.info/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm hover:text-white">
                  <MapPin className="w-4 h-4" />
                  Sito Ufficiale
                </a>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-bold mb-4">Link Utili</h3>
              <div className="space-y-2">
                <a href="#soluzioni" className="block text-sm hover:text-white">Soluzioni</a>
                <a href="#metodo" className="block text-sm hover:text-white">Metodo PEP</a>
                <a href="#faq" className="block text-sm hover:text-white">FAQ</a>
                <a href="/privacy-policy" className="block text-sm hover:text-white">Privacy Policy</a>
                <a href="/cookie-policy" className="block text-sm hover:text-white">Cookie Policy</a>
              </div>
            </div>
          </div>

          <div className="border-t border-white/20 pt-8 text-center text-sm text-gray-300">
            <p>© 2024 Alt Bollette SRL. Tutti i diritti riservati.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PremiumHomePage;
